/**
 * # Reading a menu-governance refusal
 *
 * `MenuVersionService` refuses almost everything with a plain `ConflictException`
 * / `UnprocessableEntityException` / `ForbiddenException` string body rather
 * than the `{ message, code }` shape `Pages/Pricing` reads — see
 * `menuGovernanceRoutes.ts` for why. Without a code to switch on, this file
 * branches on **HTTP status**, which is coarser but is genuinely all the wire
 * carries:
 *
 *  - `409` — a status conflict: not editable any more, already effective,
 *    changed by someone else mid-request, already rolled back. All four read
 *    the same to an operator: reload, the world moved under you.
 *  - `422` — a domain refusal on the body: no rules to submit, a rule
 *    supplying one language and not the other, a price at the wrong currency
 *    scale.
 *  - `403` — only reachable on `approve`/`reject`, and only when the actor
 *    drafted the version themselves. The screen already disables the button
 *    for that case (`standing()` in `VersionDetail.tsx`, mirroring
 *    `ApprovalDetail.tsx`'s), so a 403 here means the client's own copy of
 *    `createdById` was stale — reload.
 *  - `404` — the version, rule or branch named in the URL is gone.
 *
 * `serverMessage` is never rendered — kept for `reportRefusal`'s dev console
 * line, on the same guard `Pages/Pricing/lib/refusals.ts` uses, so a venue's
 * production console stays empty.
 */
export interface MenuGovernanceRefusal {
  status: number | null;
  serverMessage: string;
}

export function refusalOf(error: unknown): MenuGovernanceRefusal {
  const response = (error as { response?: { status?: number; data?: Record<string, unknown> } })
    .response;
  const data = response?.data;
  const raw = data?.message;
  const serverMessage = Array.isArray(raw)
    ? raw.join(', ')
    : (typeof raw === 'string' && raw) || (error as Error | undefined)?.message || '';

  const refusal: MenuGovernanceRefusal = {
    status: response?.status ?? null,
    serverMessage,
  };
  reportRefusal(refusal);
  return refusal;
}

function reportRefusal(refusal: MenuGovernanceRefusal): void {
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.debug('[menu-governance] refused', refusal.status ?? '(no status)', refusal.serverMessage);
}

/** The i18n key under `menuGovernance.refusal` for this refusal's status. */
export function refusalKey(refusal: MenuGovernanceRefusal): string {
  switch (refusal.status) {
    case 409:
      return 'menuGovernance.refusal.versionChanged';
    case 422:
      return 'menuGovernance.refusal.notEditable';
    case 404:
      return 'menuGovernance.refusal.notFound';
    default:
      return 'menuGovernance.refusal.genericBody';
  }
}
